/**
 * UtilitiesManagePage - Configuration UI for Utility Dashboard
 *
 * Features:
 * - Create/edit/delete categories
 * - Configure root sensors with price periods
 * - Configure consumption tree
 * - Import/export config (JSON/YAML)
 */

import {
  useGetApiSettingsHomeLocation,
  useGetApiUtilitiesConfig,
  usePutApiUtilitiesConfig,
  usePostApiUtilitiesExport,
  usePostApiUtilitiesImport,
  useListSensors,
  getGetApiUtilitiesOverviewQueryKey,
  getGetApiUtilitiesCategoriesIdInsightsQueryKey,
  type UtilitiesCategory,
  type UtilitiesNode,
  type UtilitiesRootSensor,
  type UtilitiesPricePeriod,
  type UtilitiesUtilityDashboardConfig,
  type SensorsSensor,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Download,
  Folder,
  Plus,
  Save,
  Settings,
  Trash2,
  Upload,
} from 'lucide-react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { SensorSelect } from '@/components/SensorSelect';
import { Button, Modal, TextField, Select, Badge, useToast } from '@/components/ui';
import { cn } from '@/components/ui/utils';

// Simple key generator for React keys on new items (NOT an ID, just for React)
let tempKeyCounter = 0;
function generateTempKey(): string {
  return `_tmp_${++tempKeyCounter}`;
}

// Check if an ID is a temporary React key (not a real UUID)
function isTempKey(id: string | undefined): boolean {
  return id?.startsWith('_tmp_') ?? false;
}

// Helper to generate a new category - NO ID, backend will assign one
function createNewCategory(): UtilitiesCategory {
  return {
    id: generateTempKey(), // Only for React key, will be stripped before save
    name: '',
    root: {
      sensors: [],
      children: [],
    },
    nodes: [],
  };
}

// Helper to generate a new node - NO ID, backend will assign one
function createNewNode(name: string, sensorHumanId: string): UtilitiesNode {
  return {
    id: generateTempKey(), // Only for React key, will be stripped before save
    name,
    sensorHumanId,
    children: [],
  };
}

// Prepare config for backend:
// - Strip temp keys (new items get empty ID, backend generates UUID)
// - Convert temp key references in children arrays to indices
// - Keep real UUIDs unchanged
function prepareConfigForSave(
  config: UtilitiesUtilityDashboardConfig
): UtilitiesUtilityDashboardConfig {
  return {
    ...config,
    categories: (config.categories ?? []).map((category) => {
      const nodes = category.nodes ?? [];

      // Build mapping from temp keys to indices (for new nodes only)
      const tempKeyToIndex = new Map<string, string>();
      nodes.forEach((node, index) => {
        if (node.id && isTempKey(node.id)) {
          tempKeyToIndex.set(node.id, String(index));
        }
      });

      // Convert ONLY temp key references in children arrays to indices
      // Real UUIDs are kept as-is
      const convertChildren = (childIds: string[]): string[] =>
        childIds.map((id) => {
          if (isTempKey(id)) {
            return tempKeyToIndex.get(id) ?? id;
          }
          return id; // Keep real UUIDs unchanged
        });

      return {
        ...category,
        id: isTempKey(category.id) ? '' : category.id,
        root: {
          ...category.root,
          children: convertChildren(category.root?.children ?? []),
        },
        nodes: nodes.map((node) => ({
          ...node,
          id: isTempKey(node.id) ? '' : node.id,
          children: convertChildren(node.children ?? []),
        })),
      };
    }),
  };
}

// Helper to generate a new price period
function createNewPricePeriod(): UtilitiesPricePeriod {
  return {
    startsAtLocal: new Date().toISOString().slice(0, 16),
    endsAtLocal: undefined,
    pricePerUnit: 0,
  };
}

// Deep clone helper
function cloneConfig(config: UtilitiesUtilityDashboardConfig): UtilitiesUtilityDashboardConfig {
  return JSON.parse(JSON.stringify(config));
}

// Check if configs are equal
function configsEqual(
  a: UtilitiesUtilityDashboardConfig | null | undefined,
  b: UtilitiesUtilityDashboardConfig | null | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export function UtilitiesManagePage() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  // Data fetching
  const {
    data: homeLocationData,
    isLoading: isLoadingHome,
    isError: isHomeError,
  } = useGetApiSettingsHomeLocation();
  const homeLocation = homeLocationData?.location;
  const hasTimezone = Boolean(homeLocation?.timezone && homeLocation.timezone.trim() !== '');

  const {
    data: configData,
    isLoading: isLoadingConfig,
    isError: isConfigError,
    refetch: refetchConfig,
  } = useGetApiUtilitiesConfig({
    query: {
      enabled: hasTimezone,
    },
  });

  const { data: sensorsData, isLoading: isLoadingSensors } = useListSensors();
  const sensors = useMemo(() => sensorsData?.sensors ?? [], [sensorsData?.sensors]);

  // Mutations
  const saveConfigMutation = usePutApiUtilitiesConfig();
  const exportMutation = usePostApiUtilitiesExport();
  const importMutation = usePostApiUtilitiesImport();

  // Local state
  const [draft, setDraft] = useState<UtilitiesUtilityDashboardConfig | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Modal states
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [showEditNodeModal, setShowEditNodeModal] = useState(false);
  const [showDeleteNodeModal, setShowDeleteNodeModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Form states for modals
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newNodeName, setNewNodeName] = useState('');
  const [newNodeSensorId, setNewNodeSensorId] = useState<string | null>(null);
  const [newNodeParentId, setNewNodeParentId] = useState<string | null>(null); // null = root level
  const [editNodeId, setEditNodeId] = useState<string | null>(null);
  const [deleteNodeId, setDeleteNodeId] = useState<string | null>(null);
  const [importFormat, setImportFormat] = useState<'json' | 'yaml'>('yaml');
  const [importContent, setImportContent] = useState('');
  const [exportFormat, setExportFormat] = useState<'json' | 'yaml'>('yaml');

  // Initialize draft from server config
  useEffect(() => {
    if (configData?.config && !draft) {
      setDraft(cloneConfig(configData.config));
      // Auto-select first category
      const categories = configData.config.categories ?? [];
      if (categories.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(categories[0]?.id ?? null);
      }
    }
  }, [configData?.config, draft, selectedCategoryId]);

  // Derived state
  const isLoading = isLoadingHome || (hasTimezone && isLoadingConfig) || isLoadingSensors;
  const isError = isHomeError || (hasTimezone && isConfigError);
  const hasUnsavedChanges = !configsEqual(draft, configData?.config);

  const categories = useMemo(() => draft?.categories ?? [], [draft?.categories]);
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  // Get unit symbol from selected category's root sensors
  const categoryUnitSymbol = useMemo(() => {
    if (!selectedCategory?.root?.sensors?.length) return undefined;
    const firstSensorId = selectedCategory.root.sensors[0]?.sensorHumanId;
    if (!firstSensorId) return undefined;
    const sensor = sensors.find((s) => s.humanId === firstSensorId);
    return sensor?.unitSymbol;
  }, [selectedCategory, sensors]);

  // Get all sensor IDs used in category (for exclusion in picker)
  const usedSensorIds = useMemo(() => {
    if (!selectedCategory) return [];
    const ids: string[] = [];
    // Root sensors
    selectedCategory.root?.sensors?.forEach((rs) => {
      if (rs.sensorHumanId) ids.push(rs.sensorHumanId);
    });
    // Node sensors
    selectedCategory.nodes?.forEach((node) => {
      if (node.sensorHumanId) ids.push(node.sensorHumanId);
    });
    return ids;
  }, [selectedCategory]);

  // ===============================
  // Handlers
  // ===============================

  const handleSave = useCallback(async () => {
    if (!draft) return;

    try {
      const configForBackend = prepareConfigForSave(draft);
      const response = await saveConfigMutation.mutateAsync({ data: { config: configForBackend } });
      // Update draft with server response (which has real UUIDs instead of ephemeral IDs)
      if (response.config) {
        setDraft(cloneConfig(response.config));
        // Update selected category ID to the new UUID if it was ephemeral
        const savedCategories = response.config.categories ?? [];
        if (selectedCategoryId && isTempKey(selectedCategoryId) && savedCategories.length > 0) {
          // Find category by name since ID changed
          const selectedCat = draft.categories?.find((c) => c.id === selectedCategoryId);
          const savedCat = savedCategories.find((c) => c.name === selectedCat?.name);
          if (savedCat?.id) {
            setSelectedCategoryId(savedCat.id);
          }
        }
      }
      // Invalidate resource queries so overview/insights pages refetch with new config
      await queryClient.invalidateQueries({ queryKey: getGetApiUtilitiesOverviewQueryKey() });
      // Invalidate all category insights queries (we don't know which specific ones are cached)
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === getGetApiUtilitiesCategoriesIdInsightsQueryKey('').at(0),
      });
      addToast({ variant: 'success', message: 'Configuration saved' });
    } catch (error) {
      addToast({
        variant: 'error',
        message: error instanceof Error ? error.message : 'Failed to save configuration',
      });
    }
  }, [draft, selectedCategoryId, saveConfigMutation, addToast, queryClient]);

  const handleDiscardChanges = useCallback(() => {
    if (configData?.config) {
      setDraft(cloneConfig(configData.config));
    }
  }, [configData?.config]);

  // Category handlers
  const handleAddCategory = useCallback(() => {
    if (!newCategoryName.trim()) return;

    // Check for duplicate name
    const exists = categories.some(
      (c) => c.name?.toLowerCase() === newCategoryName.trim().toLowerCase()
    );
    if (exists) {
      addToast({ variant: 'error', message: 'A category with this name already exists' });
      return;
    }

    const newCategory = createNewCategory();
    newCategory.name = newCategoryName.trim();

    // Handle case where draft is null (new config with no categories yet)
    const currentDraft = draft ?? { categories: [] };

    setDraft({
      ...currentDraft,
      categories: [...categories, newCategory],
    });
    setSelectedCategoryId(newCategory.id ?? null);
    setShowAddCategoryModal(false);
    setNewCategoryName('');
  }, [draft, newCategoryName, categories, addToast]);

  const handleDeleteCategory = useCallback(() => {
    if (!draft || !selectedCategoryId) return;

    setDraft({
      ...draft,
      categories: categories.filter((c) => c.id !== selectedCategoryId),
    });
    setSelectedCategoryId(categories.length > 1 ? (categories[0]?.id ?? null) : null);
    setShowDeleteCategoryModal(false);
  }, [draft, selectedCategoryId, categories]);

  const handleUpdateCategoryName = useCallback(
    (name: string) => {
      if (!draft || !selectedCategoryId) return;

      setDraft({
        ...draft,
        categories: categories.map((c) => (c.id === selectedCategoryId ? { ...c, name } : c)),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  // Root sensor handlers
  const handleAddRootSensor = useCallback(
    (sensorHumanId: string) => {
      if (!draft || !selectedCategoryId || !sensorHumanId) return;

      const newRootSensor: UtilitiesRootSensor = {
        sensorHumanId,
        prices: [],
      };

      setDraft({
        ...draft,
        categories: categories.map((c) => {
          if (c.id !== selectedCategoryId) return c;
          return {
            ...c,
            root: {
              ...c.root,
              sensors: [...(c.root?.sensors ?? []), newRootSensor],
            },
          };
        }),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  const handleRemoveRootSensor = useCallback(
    (sensorHumanId: string) => {
      if (!draft || !selectedCategoryId) return;

      setDraft({
        ...draft,
        categories: categories.map((c) => {
          if (c.id !== selectedCategoryId) return c;
          return {
            ...c,
            root: {
              ...c.root,
              sensors: (c.root?.sensors ?? []).filter((s) => s.sensorHumanId !== sensorHumanId),
            },
          };
        }),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  // Price period handlers
  const handleAddPricePeriod = useCallback(
    (sensorHumanId: string) => {
      if (!draft || !selectedCategoryId) return;

      const newPeriod = createNewPricePeriod();

      setDraft({
        ...draft,
        categories: categories.map((c) => {
          if (c.id !== selectedCategoryId) return c;
          return {
            ...c,
            root: {
              ...c.root,
              sensors: (c.root?.sensors ?? []).map((s) =>
                s.sensorHumanId === sensorHumanId
                  ? { ...s, prices: [...(s.prices ?? []), newPeriod] }
                  : s
              ),
            },
          };
        }),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  const handleUpdatePricePeriod = useCallback(
    (sensorHumanId: string, index: number, updates: Partial<UtilitiesPricePeriod>) => {
      if (!draft || !selectedCategoryId) return;

      setDraft({
        ...draft,
        categories: categories.map((c) => {
          if (c.id !== selectedCategoryId) return c;
          return {
            ...c,
            root: {
              ...c.root,
              sensors: (c.root?.sensors ?? []).map((s) => {
                if (s.sensorHumanId !== sensorHumanId) return s;
                const newPrices = [...(s.prices ?? [])];
                newPrices[index] = { ...newPrices[index], ...updates };
                // Sort by start date
                newPrices.sort((a, b) =>
                  (a.startsAtLocal ?? '').localeCompare(b.startsAtLocal ?? '')
                );
                return { ...s, prices: newPrices };
              }),
            },
          };
        }),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  const handleDeletePricePeriod = useCallback(
    (sensorHumanId: string, index: number) => {
      if (!draft || !selectedCategoryId) return;

      setDraft({
        ...draft,
        categories: categories.map((c) => {
          if (c.id !== selectedCategoryId) return c;
          return {
            ...c,
            root: {
              ...c.root,
              sensors: (c.root?.sensors ?? []).map((s) => {
                if (s.sensorHumanId !== sensorHumanId) return s;
                return { ...s, prices: (s.prices ?? []).filter((_, i) => i !== index) };
              }),
            },
          };
        }),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  // Node handlers
  const handleOpenAddNode = useCallback((parentId: string | null) => {
    setNewNodeParentId(parentId);
    setNewNodeName('');
    setNewNodeSensorId(null);
    setShowAddNodeModal(true);
  }, []);

  const handleAddNode = useCallback(() => {
    if (!draft || !selectedCategoryId || !newNodeName.trim() || !newNodeSensorId) return;

    // Check for duplicate name
    const exists =
      selectedCategory?.name?.toLowerCase() === newNodeName.trim().toLowerCase() ||
      selectedCategory?.nodes?.some(
        (n) => n.name?.toLowerCase() === newNodeName.trim().toLowerCase()
      );
    if (exists) {
      addToast({
        variant: 'error',
        message: 'A node with this name already exists in this category',
      });
      return;
    }

    const newNode = createNewNode(newNodeName.trim(), newNodeSensorId);

    setDraft({
      ...draft,
      categories: categories.map((c) => {
        if (c.id !== selectedCategoryId) return c;

        // Add node to the flat nodes array
        const updatedNodes = [...(c.nodes ?? []), newNode];

        // Update parent's children list
        let updatedRoot = c.root;
        if (newNodeParentId === null) {
          // Add to root children
          updatedRoot = {
            ...c.root,
            children: [...(c.root?.children ?? []), newNode.id!],
          };
        } else {
          // Add to parent node's children
          const updateNodeChildren = (nodes: UtilitiesNode[]): UtilitiesNode[] =>
            nodes.map((n) =>
              n.id === newNodeParentId
                ? { ...n, children: [...(n.children ?? []), newNode.id!] }
                : n
            );
          return {
            ...c,
            nodes: updateNodeChildren(updatedNodes),
            root: updatedRoot,
          };
        }

        return { ...c, nodes: updatedNodes, root: updatedRoot };
      }),
    });

    setShowAddNodeModal(false);
    setNewNodeName('');
    setNewNodeSensorId(null);
    setNewNodeParentId(null);
  }, [
    draft,
    selectedCategoryId,
    selectedCategory,
    newNodeName,
    newNodeSensorId,
    newNodeParentId,
    categories,
    addToast,
  ]);

  const handleOpenEditNode = useCallback(
    (nodeId: string) => {
      const node = selectedCategory?.nodes?.find((n) => n.id === nodeId);
      if (!node) return;

      setEditNodeId(nodeId);
      setNewNodeName(node.name ?? '');
      setNewNodeSensorId(node.sensorHumanId ?? null);
      setShowEditNodeModal(true);
    },
    [selectedCategory]
  );

  const handleUpdateNode = useCallback(() => {
    if (!draft || !selectedCategoryId || !editNodeId || !newNodeName.trim() || !newNodeSensorId)
      return;

    // Check for duplicate name (excluding current node)
    const exists =
      selectedCategory?.name?.toLowerCase() === newNodeName.trim().toLowerCase() ||
      selectedCategory?.nodes?.some(
        (n) => n.id !== editNodeId && n.name?.toLowerCase() === newNodeName.trim().toLowerCase()
      );
    if (exists) {
      addToast({
        variant: 'error',
        message: 'A node with this name already exists in this category',
      });
      return;
    }

    setDraft({
      ...draft,
      categories: categories.map((c) => {
        if (c.id !== selectedCategoryId) return c;
        return {
          ...c,
          nodes: (c.nodes ?? []).map((n) =>
            n.id === editNodeId
              ? { ...n, name: newNodeName.trim(), sensorHumanId: newNodeSensorId }
              : n
          ),
        };
      }),
    });

    setShowEditNodeModal(false);
    setEditNodeId(null);
    setNewNodeName('');
    setNewNodeSensorId(null);
  }, [
    draft,
    selectedCategoryId,
    selectedCategory,
    editNodeId,
    newNodeName,
    newNodeSensorId,
    categories,
    addToast,
  ]);

  const handleOpenDeleteNode = useCallback((nodeId: string) => {
    setDeleteNodeId(nodeId);
    setShowDeleteNodeModal(true);
  }, []);

  const handleDeleteNode = useCallback(() => {
    if (!draft || !selectedCategoryId || !deleteNodeId) return;

    // Helper to recursively collect all descendant IDs
    const collectDescendants = (nodeId: string, nodes: UtilitiesNode[]): string[] => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return [nodeId];
      const descendants = [nodeId];
      for (const childId of node.children ?? []) {
        descendants.push(...collectDescendants(childId, nodes));
      }
      return descendants;
    };

    setDraft({
      ...draft,
      categories: categories.map((c) => {
        if (c.id !== selectedCategoryId) return c;

        const nodesToDelete = new Set(collectDescendants(deleteNodeId, c.nodes ?? []));

        // Remove from root children
        const newRootChildren = (c.root?.children ?? []).filter((id) => !nodesToDelete.has(id));

        // Remove from nodes array and update parent references
        const newNodes = (c.nodes ?? [])
          .filter((n) => !nodesToDelete.has(n.id!))
          .map((n) => ({
            ...n,
            children: (n.children ?? []).filter((id) => !nodesToDelete.has(id)),
          }));

        return {
          ...c,
          nodes: newNodes,
          root: { ...c.root, children: newRootChildren },
        };
      }),
    });

    setShowDeleteNodeModal(false);
    setDeleteNodeId(null);
  }, [draft, selectedCategoryId, deleteNodeId, categories]);

  const handleMoveNode = useCallback(
    (nodeId: string, direction: 'up' | 'down') => {
      if (!draft || !selectedCategoryId) return;

      setDraft({
        ...draft,
        categories: categories.map((c) => {
          if (c.id !== selectedCategoryId) return c;

          // Helper to move item in array
          const moveInArray = (arr: string[], id: string, dir: 'up' | 'down'): string[] => {
            const index = arr.indexOf(id);
            if (index === -1) return arr;
            const newIndex = dir === 'up' ? index - 1 : index + 1;
            if (newIndex < 0 || newIndex >= arr.length) return arr;
            const newArr = [...arr];
            const temp = newArr[index];
            newArr[index] = newArr[newIndex]!;
            newArr[newIndex] = temp!;
            return newArr;
          };

          // Check if node is in root children
          if (c.root?.children?.includes(nodeId)) {
            return {
              ...c,
              root: {
                ...c.root,
                children: moveInArray(c.root.children, nodeId, direction),
              },
            };
          }

          // Find parent and move within its children
          return {
            ...c,
            nodes: (c.nodes ?? []).map((n) => {
              if (!n.children?.includes(nodeId)) return n;
              return {
                ...n,
                children: moveInArray(n.children, nodeId, direction),
              };
            }),
          };
        }),
      });
    },
    [draft, selectedCategoryId, categories]
  );

  // Import/Export handlers
  const handleExport = useCallback(async () => {
    try {
      const result = await exportMutation.mutateAsync({ data: { format: exportFormat } });
      if (result.content && result.filename) {
        // Create and download file
        const blob = new Blob([result.content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        addToast({ variant: 'success', message: 'Configuration exported' });
      }
    } catch (error) {
      addToast({
        variant: 'error',
        message: error instanceof Error ? error.message : 'Failed to export configuration',
      });
    }
  }, [exportFormat, exportMutation, addToast]);

  const handleImport = useCallback(async () => {
    if (!importContent.trim()) {
      addToast({ variant: 'error', message: 'Please provide content to import' });
      return;
    }

    try {
      await importMutation.mutateAsync({
        data: { content: importContent, format: importFormat },
      });
      addToast({ variant: 'success', message: 'Configuration imported' });
      setShowImportModal(false);
      setImportContent('');
      // Refresh config
      refetchConfig();
      setDraft(null); // Reset draft to reload from server
    } catch (error) {
      addToast({
        variant: 'error',
        message: error instanceof Error ? error.message : 'Failed to import configuration',
      });
    }
  }, [importContent, importFormat, importMutation, addToast, refetchConfig]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setImportContent(content);
    };
    reader.readAsText(file);
  }, []);

  // Toggle node expansion
  const toggleNodeExpansion = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // ===============================
  // Render
  // ===============================

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
        <p className="text-sm text-red-600 dark:text-red-400">
          Failed to load utility configuration
        </p>
      </div>
    );
  }

  // Missing timezone guard
  if (!hasTimezone) {
    return (
      <div className="space-y-6">
        <Header />
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Set your home timezone
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-zinc-500">
            The Utility Dashboard needs a timezone configured to correctly determine day and month
            boundaries for usage tracking and cost calculations.
          </p>
          <Link to={ROUTES.SETTINGS_HOME_LOCATION}>
            <Button className="mt-6">Configure Home Location</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        onDiscard={handleDiscardChanges}
        isSaving={saveConfigMutation.isPending}
      />

      {/* Main content: Categories list + Editor */}
      <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
        {/* Categories List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Categories</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowAddCategoryModal(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Add
            </Button>
          </div>

          {categories.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-white/[0.1]">
              <Folder className="mx-auto mb-2 h-8 w-8 text-gray-400 dark:text-zinc-600" />
              <p className="text-sm text-gray-500 dark:text-zinc-500">No categories yet</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setShowAddCategoryModal(true)}
              >
                Create category
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              {categories.map((category) => {
                const rootSensorsCount = category.root?.sensors?.length ?? 0;
                const nodesCount = category.nodes?.length ?? 0;
                const firstSensor = category.root?.sensors?.[0]?.sensorHumanId;
                const unitSymbol = firstSensor
                  ? sensors.find((s) => s.humanId === firstSensor)?.unitSymbol
                  : undefined;

                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategoryId(category.id ?? null)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors',
                      selectedCategoryId === category.id
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-500/50 dark:bg-blue-500/10'
                        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.06] dark:bg-[#18181b] dark:hover:border-white/[0.1]'
                    )}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {category.name || 'Unnamed'}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
                        <span>
                          {rootSensorsCount} sensor{rootSensorsCount !== 1 ? 's' : ''}
                        </span>
                        <span>•</span>
                        <span>
                          {nodesCount} node{nodesCount !== 1 ? 's' : ''}
                        </span>
                        {unitSymbol && (
                          <>
                            <span>•</span>
                            <span>{unitSymbol}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Category Editor */}
        <div className="space-y-6">
          {selectedCategory ? (
            <>
              {/* Category Name */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
                <div className="flex items-start justify-between gap-4">
                  <TextField
                    label="Category Name"
                    value={selectedCategory.name ?? ''}
                    onChange={(e) => handleUpdateCategoryName(e.target.value)}
                    placeholder="e.g., Electricity"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteCategoryModal(true)}
                    className="mt-7 text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Root Sensors */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Root Sensors
                  </h3>
                  {categoryUnitSymbol && (
                    <Badge variant="default" size="sm">
                      Unit: {categoryUnitSymbol}
                    </Badge>
                  )}
                </div>

                {(selectedCategory.root?.sensors?.length ?? 0) === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-zinc-500">
                    Add at least one root sensor to track total consumption.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {selectedCategory.root?.sensors?.map((rootSensor) => {
                      const sensor = sensors.find((s) => s.humanId === rootSensor.sensorHumanId);
                      return (
                        <div
                          key={rootSensor.sensorHumanId}
                          className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-800/50"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {sensor?.name ?? rootSensor.sensorHumanId}
                              </div>
                              <div className="text-xs font-mono text-gray-500 dark:text-zinc-500">
                                {rootSensor.sensorHumanId}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveRootSensor(rootSensor.sensorHumanId ?? '')}
                              className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Price Periods */}
                          <details className="mt-3">
                            <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-zinc-300">
                              Pricing ({rootSensor.prices?.length ?? 0} period
                              {(rootSensor.prices?.length ?? 0) !== 1 ? 's' : ''})
                            </summary>
                            <div className="mt-3 space-y-2">
                              {rootSensor.prices?.map((period, periodIndex) => (
                                <div
                                  key={periodIndex}
                                  className="flex flex-wrap items-end gap-2 rounded border border-gray-200 bg-white p-2 dark:border-white/[0.06] dark:bg-zinc-900"
                                >
                                  <div className="flex-1 min-w-[160px]">
                                    <label className="mb-1 block text-xs text-gray-500 dark:text-zinc-500">
                                      Starts at
                                    </label>
                                    <input
                                      type="datetime-local"
                                      value={period.startsAtLocal ?? ''}
                                      onChange={(e) =>
                                        handleUpdatePricePeriod(
                                          rootSensor.sensorHumanId ?? '',
                                          periodIndex,
                                          { startsAtLocal: e.target.value }
                                        )
                                      }
                                      className="w-full rounded border border-gray-300 bg-gray-50 px-2 py-1.5 text-sm dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-[160px]">
                                    <label className="mb-1 block text-xs text-gray-500 dark:text-zinc-500">
                                      Ends at (optional)
                                    </label>
                                    <input
                                      type="datetime-local"
                                      value={period.endsAtLocal ?? ''}
                                      onChange={(e) =>
                                        handleUpdatePricePeriod(
                                          rootSensor.sensorHumanId ?? '',
                                          periodIndex,
                                          { endsAtLocal: e.target.value || undefined }
                                        )
                                      }
                                      className="w-full rounded border border-gray-300 bg-gray-50 px-2 py-1.5 text-sm dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white"
                                    />
                                  </div>
                                  <div className="w-[120px]">
                                    <label className="mb-1 block text-xs text-gray-500 dark:text-zinc-500">
                                      Price per unit
                                    </label>
                                    <input
                                      type="number"
                                      step="any"
                                      value={period.pricePerUnit ?? 0}
                                      onChange={(e) =>
                                        handleUpdatePricePeriod(
                                          rootSensor.sensorHumanId ?? '',
                                          periodIndex,
                                          { pricePerUnit: parseFloat(e.target.value) || 0 }
                                        )
                                      }
                                      className="w-full rounded border border-gray-300 bg-gray-50 px-2 py-1.5 text-sm dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white"
                                    />
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleDeletePricePeriod(
                                        rootSensor.sensorHumanId ?? '',
                                        periodIndex
                                      )
                                    }
                                    className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddPricePeriod(rootSensor.sensorHumanId ?? '')}
                              >
                                <Plus className="mr-1.5 h-4 w-4" />
                                Add period
                              </Button>
                              <p className="text-xs text-gray-500 dark:text-zinc-500">
                                Times are interpreted in your Home timezone.
                              </p>
                            </div>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add root sensor */}
                <div className="mt-4">
                  <SensorSelect
                    label="Add root sensor"
                    value={null}
                    onChange={(value) => {
                      if (value) handleAddRootSensor(value);
                    }}
                    sensors={sensors}
                    placeholder="Select a sensor..."
                    unitSymbol={categoryUnitSymbol}
                    excludeIds={usedSensorIds}
                  />
                </div>
              </div>

              {/* Consumption Tree */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Consumption Tree
                  </h3>
                  <Button variant="outline" size="sm" onClick={() => handleOpenAddNode(null)}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add node
                  </Button>
                </div>

                {(selectedCategory.nodes?.length ?? 0) === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-zinc-500">
                    Add nodes to break down consumption by sub-meter or circuit.
                  </p>
                ) : (
                  <TreeView
                    category={selectedCategory}
                    sensors={sensors}
                    expandedNodes={expandedNodes}
                    onToggleExpand={toggleNodeExpansion}
                    onAddChild={handleOpenAddNode}
                    onEdit={handleOpenEditNode}
                    onDelete={handleOpenDeleteNode}
                    onMove={handleMoveNode}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-gray-300 p-8 dark:border-white/[0.1]">
              <div className="text-center">
                <Settings className="mx-auto mb-3 h-10 w-10 text-gray-400 dark:text-zinc-600" />
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  Select a category to edit or create a new one
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Import/Export Section */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
        <h3 className="mb-4 text-sm font-medium text-gray-900 dark:text-white">Import / Export</h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-end gap-2">
            <Select
              label="Export format"
              value={exportFormat}
              onChange={(v) => setExportFormat(v as 'json' | 'yaml')}
              options={[
                { value: 'yaml', label: 'YAML' },
                { value: 'json', label: 'JSON' },
              ]}
            />
            <Button variant="outline" onClick={handleExport} disabled={exportMutation.isPending}>
              <Download className="mr-1.5 h-4 w-4" />
              Export
            </Button>
          </div>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="mr-1.5 h-4 w-4" />
            Import
          </Button>
        </div>
      </div>

      {/* Modals */}
      <AddCategoryModal
        isOpen={showAddCategoryModal}
        onClose={() => {
          setShowAddCategoryModal(false);
          setNewCategoryName('');
        }}
        name={newCategoryName}
        onNameChange={setNewCategoryName}
        onAdd={handleAddCategory}
      />

      <DeleteConfirmModal
        isOpen={showDeleteCategoryModal}
        onClose={() => setShowDeleteCategoryModal(false)}
        onConfirm={handleDeleteCategory}
        title="Delete category"
        message={`Are you sure you want to delete "${selectedCategory?.name}"? This will remove all nodes and pricing data.`}
      />

      <AddNodeModal
        isOpen={showAddNodeModal}
        onClose={() => {
          setShowAddNodeModal(false);
          setNewNodeName('');
          setNewNodeSensorId(null);
          setNewNodeParentId(null);
        }}
        name={newNodeName}
        onNameChange={setNewNodeName}
        sensorId={newNodeSensorId}
        onSensorChange={setNewNodeSensorId}
        sensors={sensors}
        unitSymbol={categoryUnitSymbol}
        excludeIds={usedSensorIds}
        onAdd={handleAddNode}
      />

      <EditNodeModal
        isOpen={showEditNodeModal}
        onClose={() => {
          setShowEditNodeModal(false);
          setEditNodeId(null);
          setNewNodeName('');
          setNewNodeSensorId(null);
        }}
        name={newNodeName}
        onNameChange={setNewNodeName}
        sensorId={newNodeSensorId}
        onSensorChange={setNewNodeSensorId}
        sensors={sensors}
        unitSymbol={categoryUnitSymbol}
        excludeIds={usedSensorIds.filter((id) => {
          const node = selectedCategory?.nodes?.find((n) => n.id === editNodeId);
          return id !== node?.sensorHumanId;
        })}
        onSave={handleUpdateNode}
      />

      <DeleteConfirmModal
        isOpen={showDeleteNodeModal}
        onClose={() => {
          setShowDeleteNodeModal(false);
          setDeleteNodeId(null);
        }}
        onConfirm={handleDeleteNode}
        title="Delete node"
        message="Are you sure you want to delete this node? All child nodes will also be deleted."
      />

      <ImportModal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportContent('');
        }}
        format={importFormat}
        onFormatChange={(v) => setImportFormat(v as 'json' | 'yaml')}
        content={importContent}
        onContentChange={setImportContent}
        onFileUpload={handleFileUpload}
        onImport={handleImport}
        isImporting={importMutation.isPending}
      />
    </div>
  );
}

// ===============================
// Sub-components
// ===============================

interface HeaderProps {
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  isSaving?: boolean;
}

function Header({ hasUnsavedChanges, onSave, onDiscard, isSaving }: HeaderProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <Link
          to={ROUTES.UTILITIES}
          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Manage Utilities
          </h1>
          <p className="mt-0.5 text-[15px] text-gray-500 dark:text-zinc-500">
            Configure categories, sensors, and pricing
          </p>
        </div>
      </div>

      {hasUnsavedChanges && (
        <div className="flex items-center gap-2">
          <Badge variant="warning" size="sm">
            Unsaved changes
          </Badge>
          <Button variant="ghost" size="sm" onClick={onDiscard}>
            Discard
          </Button>
          <Button size="sm" onClick={onSave} disabled={isSaving}>
            <Save className="mr-1.5 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

interface TreeViewProps {
  category: UtilitiesCategory;
  sensors: { humanId?: string; name?: string; unitSymbol?: string }[];
  expandedNodes: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  onAddChild: (parentId: string | null) => void;
  onEdit: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
  onMove: (nodeId: string, direction: 'up' | 'down') => void;
}

function TreeView({
  category,
  sensors,
  expandedNodes,
  onToggleExpand,
  onAddChild,
  onEdit,
  onDelete,
  onMove,
}: TreeViewProps) {
  const rootChildren = category.root?.children ?? [];
  const nodes = category.nodes ?? [];

  const renderNode = (nodeId: string, depth: number, siblingIds: string[]): React.ReactNode => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return null;

    const sensor = sensors.find((s) => s.humanId === node.sensorHumanId);
    const hasChildren = (node.children?.length ?? 0) > 0;
    const isExpanded = expandedNodes.has(nodeId);
    const index = siblingIds.indexOf(nodeId);
    const isFirst = index === 0;
    const isLast = index === siblingIds.length - 1;

    return (
      <div key={nodeId}>
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.04]',
            depth > 0 && 'ml-6'
          )}
        >
          {hasChildren ? (
            <button
              onClick={() => onToggleExpand(nodeId)}
              className="rounded p-0.5 text-gray-500 hover:bg-gray-200 dark:text-zinc-500 dark:hover:bg-white/[0.06]"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {node.name ?? 'Unnamed'}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
              <span className="truncate font-mono">{node.sensorHumanId}</span>
              {sensor?.unitSymbol && (
                <>
                  <span>•</span>
                  <span>{sensor.unitSymbol}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onMove(nodeId, 'up')}
              disabled={isFirst}
              className={cn(
                'rounded p-1 text-gray-400 transition-colors',
                isFirst
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-white'
              )}
              title="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              onClick={() => onMove(nodeId, 'down')}
              disabled={isLast}
              className={cn(
                'rounded p-1 text-gray-400 transition-colors',
                isLast
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-white'
              )}
              title="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <button
              onClick={() => onAddChild(nodeId)}
              className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-white"
              title="Add child"
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              onClick={() => onEdit(nodeId)}
              className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-white"
              title="Edit"
            >
              <Settings className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(nodeId)}
              className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((childId) => renderNode(childId, depth + 1, node.children!))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {rootChildren.map((nodeId) => renderNode(nodeId, 0, rootChildren))}
    </div>
  );
}

// Modal components

interface AddCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  onNameChange: (name: string) => void;
  onAdd: () => void;
}

function AddCategoryModal({ isOpen, onClose, name, onNameChange, onAdd }: AddCategoryModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Category"
      subtitle="Create a new utility category"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onAdd} disabled={!name.trim()}>
            Add Category
          </Button>
        </>
      }
    >
      <form
        id="add-category-form"
        onSubmit={(e) => {
          e.preventDefault();
          onAdd();
        }}
      >
        <TextField
          label="Category Name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g., Electricity, Gas, Water"
          autoFocus
        />
      </form>
    </Modal>
  );
}

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
}: DeleteConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
          >
            Delete
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">{message}</p>
    </Modal>
  );
}

interface AddNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  onNameChange: (name: string) => void;
  sensorId: string | null;
  onSensorChange: (id: string | null) => void;
  sensors: SensorsSensor[];
  unitSymbol?: string;
  excludeIds: string[];
  onAdd: () => void;
}

function AddNodeModal({
  isOpen,
  onClose,
  name,
  onNameChange,
  sensorId,
  onSensorChange,
  sensors,
  unitSymbol,
  excludeIds,
  onAdd,
}: AddNodeModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Node"
      subtitle="Add a sub-meter or circuit to track"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onAdd} disabled={!name.trim() || !sensorId}>
            Add Node
          </Button>
        </>
      }
    >
      <form
        id="add-node-form"
        onSubmit={(e) => {
          e.preventDefault();
          onAdd();
        }}
        className="space-y-4"
      >
        <TextField
          label="Node Name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g., Kitchen, Office"
          autoFocus
        />
        <SensorSelect
          label="Sensor"
          value={sensorId}
          onChange={onSensorChange}
          sensors={sensors}
          placeholder="Select a sensor..."
          unitSymbol={unitSymbol}
          excludeIds={excludeIds}
        />
      </form>
    </Modal>
  );
}

interface EditNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  onNameChange: (name: string) => void;
  sensorId: string | null;
  onSensorChange: (id: string | null) => void;
  sensors: SensorsSensor[];
  unitSymbol?: string;
  excludeIds: string[];
  onSave: () => void;
}

function EditNodeModal({
  isOpen,
  onClose,
  name,
  onNameChange,
  sensorId,
  onSensorChange,
  sensors,
  unitSymbol,
  excludeIds,
  onSave,
}: EditNodeModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Node"
      subtitle="Update node details"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!name.trim() || !sensorId}>
            Save Changes
          </Button>
        </>
      }
    >
      <form
        id="edit-node-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}
        className="space-y-4"
      >
        <TextField
          label="Node Name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g., Kitchen, Office"
          autoFocus
        />
        <SensorSelect
          label="Sensor"
          value={sensorId}
          onChange={onSensorChange}
          sensors={sensors}
          placeholder="Select a sensor..."
          unitSymbol={unitSymbol}
          excludeIds={excludeIds}
        />
      </form>
    </Modal>
  );
}

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  format: 'json' | 'yaml';
  onFormatChange: (format: string) => void;
  content: string;
  onContentChange: (content: string) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImport: () => void;
  isImporting: boolean;
}

function ImportModal({
  isOpen,
  onClose,
  format,
  onFormatChange,
  content,
  onContentChange,
  onFileUpload,
  onImport,
  isImporting,
}: ImportModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Configuration"
      subtitle="This will replace your entire configuration"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onImport} disabled={!content.trim() || isImporting}>
            {isImporting ? 'Importing...' : 'Import and Replace'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Importing will replace your entire existing configuration.
        </div>

        <Select
          label="Format"
          value={format}
          onChange={onFormatChange}
          options={[
            { value: 'yaml', label: 'YAML' },
            { value: 'json', label: 'JSON' },
          ]}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Upload file
          </label>
          <input
            type="file"
            accept=".json,.yaml,.yml"
            onChange={onFileUpload}
            className="w-full text-sm text-gray-500 file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200 dark:text-zinc-500 dark:file:bg-zinc-800 dark:file:text-zinc-300 dark:hover:file:bg-zinc-700"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Or paste content
          </label>
          <textarea
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            rows={10}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:placeholder-zinc-500"
            placeholder={
              format === 'yaml'
                ? 'categories:\n  - name: Electricity\n    ...'
                : '{\n  "categories": [...]\n}'
            }
          />
        </div>
      </div>
    </Modal>
  );
}
